import { motion } from "framer-motion";
import { Phone, ArrowDownRight } from "lucide-react";
import { BRAND } from "@/lib/brand";
import { useT } from "@/lib/i18n";
import { trackEvent } from "@/lib/analytics";

const HERO_IMG =
  "https://images.unsplash.com/photo-1528569937393-ee892b976859?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwyfHxvbGQlMjBmYW1pbHklMjBwaG90byUyMG1lbW9yeXxlbnwwfHx8fDE3ODE0MjQ1ODF8MA&ixlib=rb-4.1.0&q=85";

export default function Hero() {
  const t = useT();
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-end overflow-hidden grain"
    >
      <div className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Looking through an old family photo album"
          className="w-full h-full object-cover object-center scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/80 to-[#0A0A0A]/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/70 via-transparent to-transparent" />
      </div>

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 pt-32 pb-20 md:pb-28">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="text-xs sm:text-sm tracking-[0.35em] uppercase text-[#D4AF37] font-semibold mb-6"
          data-testid="hero-eyebrow"
        >
          {t.brand.studio} — {t.hero.eyebrow}
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="font-serif-display text-5xl sm:text-6xl md:text-7xl lg:text-[88px] leading-[1.02] tracking-tighter text-[#F5F5F0] max-w-4xl"
          data-testid="hero-headline"
        >
          {t.hero.headlineA}
          <br />
          {t.hero.headlineB}{" "}
          <em className="text-[#D4AF37] not-italic font-medium">{t.hero.headlineC}</em>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 max-w-xl text-base md:text-lg text-[#A3A3A3] leading-relaxed"
          data-testid="hero-subhead"
        >
          {t.hero.subhead}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
        >
          <a
            href={BRAND.phoneHref}
            data-testid="hero-call-button"
            onClick={() => trackEvent("call_click", { source: "hero" })}
            className="group inline-flex items-center gap-3 bg-[#D4AF37] hover:bg-[#C49A26] text-[#0A0A0A] px-7 py-4 rounded-sm font-semibold tracking-wide transition-all hover:-translate-y-0.5 hover:shadow-[0_16px_40px_-12px_rgba(212,175,55,0.7)]"
          >
            <Phone className="w-5 h-5" strokeWidth={2.2} />
            {t.hero.callPrefix} {BRAND.phone}
          </a>
          <a
            href="#contact"
            data-testid="hero-secondary-cta"
            className="inline-flex items-center gap-2 text-sm tracking-wide text-[#F5F5F0] hover:text-[#D4AF37] transition border-b border-[#2A2A2A] hover:border-[#D4AF37] pb-2"
          >
            {t.hero.seePricing}
            <ArrowDownRight className="w-4 h-4" />
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 0.7 }}
          className="mt-16 md:mt-20 grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-6 max-w-2xl"
          data-testid="hero-stats"
        >
          {t.hero.stats.map((s) => (
            <div key={s.v} className="border-l border-[#2A2A2A] pl-4">
              <div className="font-serif-display text-2xl md:text-3xl text-[#F5F5F0]">{s.k}</div>
              <div className="text-[11px] tracking-[0.2em] uppercase text-[#A3A3A3] mt-1">{s.v}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
